import { useState } from 'react';
import useProjectStore from '../stores/projectStore';
import { useSeoChecklist } from '../hooks/useSeoChecklist';
import { useAgentIntent } from '../hooks/useAgentLibrary';

const STATUS = {
  erfuellt: { label: 'erfüllt', color: '#2d7d6f' },
  erledigt: { label: 'erledigt', color: '#0C9488' },
  offen: { label: 'offen', color: '#b8860b' },
  ungeprueft: { label: 'ungeprüft', color: '#9ca3af' },
};

function fmtDate(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

export default function SeoChecklist() {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const projects = useProjectStore((s) => s.projects);
  const current = projects.find((p) => p.id === currentProjectId);

  const { data, isLoading, refetch, isFetching } = useSeoChecklist(currentProjectId);
  const intent = useAgentIntent();
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  const groups = data?.groups || [];
  const sum = data?.summary || {};

  const check = async () => {
    setBusy(true);
    try {
      await intent.mutateAsync({ intent: 'seo.check' });
      await refetch();
    } catch { /* Status bleibt */ } finally { setBusy(false); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">SEO-Checkliste</h1>
          <p className="text-[13.5px] text-gray-500 mt-1">
            {current?.name ? <>Der OnPage-Agent prüft <span className="font-medium text-gray-700">{current.name}</span> und legt Maßnahmen an. Letzte Prüfung: {fmtDate(data?.last_run_at)}</> : 'Kein Projekt ausgewählt.'}
          </p>
        </div>
        <button
          onClick={check}
          disabled={busy}
          className="text-[13px] px-3.5 py-2 bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? 'Prüfe…' : 'Jetzt prüfen'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ['Punkte', sum.total],
          ['Erfüllt', sum.erfuellt],
          ['Offen', sum.offen],
          ['Erledigt', sum.erledigt],
          ['Ungeprüft', sum.ungeprueft],
        ].map(([label, val]) => (
          <div key={label} className="bg-white rounded-lg border border-black/[.06] px-3.5 py-2">
            <div className="text-[16px] font-semibold text-[#111f30] tabular-nums leading-none">{val ?? '–'}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-gray-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : (
        <div className="space-y-2.5">
          {groups.map((g) => {
            const isOpen = open[g.section] ?? false;
            const erf = g.items.filter((i) => i.status === 'erfuellt' || i.status === 'erledigt').length;
            return (
              <div key={g.section} className="bg-white rounded-xl border border-black/[.07] overflow-hidden">
                <button onClick={() => setOpen((o) => ({ ...o, [g.section]: !o[g.section] }))} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                  <span className={`text-[#999] text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                  <span className="font-semibold text-[14px] text-[#0D3B50] flex-1">{g.section}</span>
                  <span className="text-[11.5px] text-gray-400">{erf}/{g.items.length}</span>
                </button>
                {isOpen && (
                  <ul className="divide-y divide-gray-100 border-t border-gray-100">
                    {g.items.map((it) => {
                      const st = STATUS[it.status] || STATUS.ungeprueft;
                      return (
                        <li key={it.criterion_key} className="flex items-start gap-3 px-4 py-2.5">
                          <span className="mt-0.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: st.color }} title={st.label} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-gray-800">{it.criterion}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5 flex flex-wrap gap-x-3 items-center">
                              <span className="px-1.5 py-px rounded bg-gray-100 text-gray-500">{it.check === 'auto' ? 'Auto-Check' : 'Delegiert'}</span>
                              <span>{it.agent_label}</span>
                              {it.priority != null && <span>Prio {it.priority}</span>}
                              {it.tools && <span className="truncate">🛠 {it.tools}</span>}
                            </div>
                          </div>
                          <span className="mt-0.5 text-[10.5px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{ color: st.color, backgroundColor: st.color + '18' }}>
                            {st.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
